import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  cardRequest,
  cardSubjectPath,
  cardEvidenceLabel,
  type PassportCard as Card,
} from '../../../services/api/passportCards';
import { resolvePublicFileUrl } from '../../../utils/imageUrls';
import PassportHub, { PassportScanButton } from '../PassportHub';
import CollectorCard from '../CollectorCard';
import PassportCard from '../PassportCard';
import { PassportVisitHistory } from '../PassportPrograms';
import PassportExploreCanvas from './PassportExploreCanvas';
import {
  applyOperation,
  CollectionError,
  collectionRequest,
  queryParams,
  type CollectionResult,
  type CanvasPosition,
  type Organization,
  type Operation,
  type Group,
  type View,
} from './collectionState';
import { useCollectionMotion } from './useCollectionMotion';
import './passport-collection.css';
const initialDefaults = { view: 'binder' as View, group: 'none', sort: 'newest' };
const emptyOrg: Organization = {
  revision: 0,
  defaults: initialDefaults,
  pages: [],
  order: [],
  canvas: [],
};
const kindLabel = (kind: string) =>
  ({
    vehicle: 'Car',
    event: 'Event',
    club: 'Club',
    shop: 'Shop',
    place: 'Place',
    booth: 'Booth',
    activation: 'Activation',
    destination: 'Destination',
    checkpoint: 'Checkpoint',
    commemorative: 'Commemorative',
  })[kind] || kind;
const cardImage = (c: Card) =>
  resolvePublicFileUrl(c.design.image_url || c.subject?.image_url || '') || '';
export default function PassportCollection({ entrance = false }: { entrance?: boolean }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading)
    return (
      <div className="bc-page" role="status">
        Opening Passport…
      </div>
    );
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <CollectionContent key={user.id} entrance={entrance} />;
}
function CollectionContent({ entrance }: { entrance: boolean }) {
  const root = useRef<HTMLDivElement>(null);
  const surface = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<CollectionResult | null>(null);
  const [org, setOrg] = useState<Organization>(emptyOrg);
  const [draft, setDraft] = useState<Organization | null>(null);
  const [ops, setOps] = useState<Operation[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState('');
  const [fallback, setFallback] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);
  const [notice, setNotice] = useState('');
  const [allTypes, setAllTypes] = useState(false);
  const [filters, setFilters] = useState(false);
  const [open, setOpen] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [pageName, setPageName] = useState('');
  const [targetPage, setTargetPage] = useState('');
  const [undo, setUndo] = useState<{ state: Organization; ops: Operation[] } | null>(null);
  const [search, setSearch] = useState(params.get('q') || '');
  const [selected, setSelected] = useState<Card | null>(null);
  const [detailError, setDetailError] = useState('');
  const [back, setBack] = useState(false);
  const [showcasePicker, setShowcasePicker] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const flight = useRef<DOMRect | null>(null);
  const detailCard = useRef<HTMLDivElement>(null);
  const pushedInspector = useRef(false);
  const cache = useRef(new Map<string, Card>());
  const requestId = useRef<string | null>(null);
  const [arrival, setArrival] = useState<string | null>(null);
  const activeOrg = draft || org;
  const view = (
    ['binder', 'stacks', 'list', 'explore'].includes(params.get('view') || '')
      ? params.get('view')
      : params.get('layout') === 'list'
        ? 'list'
        : org.defaults.view
  ) as View;
  const query = useMemo(() => {
    const next = queryParams(params, org.defaults);
    if (view === 'explore') next.set('limit', '200');
    return next;
  }, [params, org.defaults, view]);
  const queryString = query.toString();
  const group = query.get('group') || 'none';
  const page = query.get('page') || '';
  const types = (query.get('types') || '').split(',').filter(Boolean);
  const selectedKey = params.get('card') || '';
  const modify = useCallback(
    (changes: Record<string, string>, replace = true) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          Object.entries(changes).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
          n.delete('cursor');
          return n;
        },
        { replace }
      ),
    [setParams]
  );
  useEffect(() => setSearch(params.get('q') || ''), [params]);
  useEffect(() => {
    if (search === (params.get('q') || '')) return;
    const timer = setTimeout(() => modify({ q: search }), 250);
    return () => clearTimeout(timer);
  }, [search, params, modify]);
  useEffect(() => {
    const id = ++generation.current;
    setBusy(true);
    setError('');
    collectionRequest<CollectionResult>(new URLSearchParams(queryString))
      .then((r) => {
        if (id !== generation.current) return;
        r.items.forEach((c) => cache.current.set(c.key!, c));
        r.showcase.forEach((c) => cache.current.set(c.key!, c));
        setData(r);
        setOrg(r.organization);
      })
      .catch((e) => {
        if (id !== generation.current) return;
        if ((e as CollectionError).code === 'binder_unavailable') setFallback(true);
        else setError(e.message);
      })
      .finally(() => {
        if (id === generation.current) setBusy(false);
      });
    return () => {
      generation.current = id + 1;
    };
  }, [queryString, revision]);
  useEffect(() => {
    const key = params.get('collected');
    const card = key && data?.items.find((c) => c.key === key);
    if (
      !card ||
      card.ownership_status !== 'active' ||
      !card.earned_at ||
      card.evidence === 'offline_pending'
    )
      return;
    const marker = `bilt:binder-arrival:${card.key}:${card.earned_at}`;
    try {
      if (sessionStorage.getItem(marker)) return;
      sessionStorage.setItem(marker, '1');
    } catch {}
    setArrival(card.key!);
  }, [data, params]);
  useEffect(() => {
    if (!selectedKey) {
      setSelected(null);
      dialog.current?.close();
      opener.current?.focus({ preventScroll: true });
      return;
    }
    let live = true;
    setBack(false);
    setDetailError('');
    setShowcasePicker(false);
    setSelected(cache.current.get(selectedKey) || null);
    if (!dialog.current?.open)
      opener.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.showModal();
    dialog.current
      ?.querySelector<HTMLButtonElement>('[data-close]')
      ?.focus({ preventScroll: true });
    cardRequest<Card>('card', undefined, { key: selectedKey })
      .then((c) => {
        if (live) {
          cache.current.set(c.key!, c);
          setSelected(c);
        }
      })
      .catch((e) => {
        if (live) setDetailError(e.message);
      });
    return () => {
      live = false;
    };
  }, [selectedKey]);
  useEffect(() => {
    if (!selected || !flight.current || !detailCard.current) return;
    const from = flight.current;
    flight.current = null;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const to = detailCard.current.getBoundingClientRect();
    if (from.bottom < 0 || from.top > window.innerHeight) return;
    detailCard.current.animate?.(
      [
        {
          transform: `translate(${from.x - to.x}px,${from.y - to.y}px) scale(${from.width / to.width})`,
          opacity: 0.6,
        },
        { transform: 'none', opacity: 1 },
      ],
      { duration: 340, easing: 'cubic-bezier(.22,1,.36,1)' }
    );
  }, [selected]);
  useEffect(() => {
    if (!ops.length) return;
    const prevent = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', prevent);
    return () => window.removeEventListener('beforeunload', prevent);
  }, [ops.length]);
  useEffect(() => {
    if (!ops.length) return;
    const guard = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest('a');
      if (
        a &&
        a.href &&
        !a.hasAttribute('download') &&
        !window.confirm('Leave without saving your arrangement?')
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('click', guard, true);
    return () => document.removeEventListener('click', guard, true);
  }, [ops.length]);
  const items = useMemo(() => {
    const cards = [...(data?.items || [])];
    if (view === 'explore') {
      const positions = new Map((activeOrg.canvas || []).map((position) => [position.key, position]));
      return cards.sort(
        (a, b) => (positions.get(a.key!)?.z ?? 0) - (positions.get(b.key!)?.z ?? 0)
      );
    }
    if (!draft) return cards;
    const ordered = page ? draft.pages.find((p) => p.id === page)?.keys : draft.order;
    if (!ordered) return cards;
    return cards
      .filter((c) => !page || ordered.includes(c.key!))
      .sort((a, b) => {
        const ai = ordered.indexOf(a.key!),
          bi = ordered.indexOf(b.key!);
        return (ai < 0 ? -1 : ai) - (bi < 0 ? -1 : bi);
      });
  }, [activeOrg.canvas, data, draft, page, view]);
  useCollectionMotion(
    root,
    `${queryString}:${view}:${open.join(',')}:${items.map((c) => c.key).join(',')}:${ops.length}`,
    // Collection filters should make the whole surface move together. Explore
    // owns its own canvas motion, so leave that view excluded from this pass.
    { maxAnimated: Infinity, excludeSelector: '.bc-explore' }
  );
  function inspect(c: Card, e?: React.MouseEvent) {
    cache.current.set(c.key!, c);
    flight.current =
      (
        e?.currentTarget.closest('[data-binder-key]') as HTMLElement | null
      )?.getBoundingClientRect() || null;
    pushedInspector.current = true;
    modify({ card: c.key! }, false);
  }
  function closeInspector() {
    if (pushedInspector.current) {
      pushedInspector.current = false;
      navigate(-1);
    } else modify({ card: '' });
  }
  function edit(op: Operation) {
    requestId.current = null;
    setUndo({ state: activeOrg, ops });
    setDraft(applyOperation(activeOrg, op));
    setOps((v) => [...v, op]);
    setSaveError('');
  }
  function seedCanvas(cards: Card[], saved: CanvasPosition[], orderedKeys: string[]): CanvasPosition[] {
    const existing = new Map(saved.map((position) => [position.key, position]));
    const keys = Array.from(new Set([...orderedKeys, ...cards.map((card) => card.key!)]));
    const columns = Math.min(6, Math.max(3, Math.ceil(Math.sqrt(Math.max(1, keys.length) * 1.25))));
    const rows = Math.max(1, Math.ceil(keys.length / columns));
    return cards.map((card) => {
      const key = card.key!;
      const found = existing.get(key);
      if (found) return found;
      const index = Math.max(0, keys.indexOf(key));
      const column = index % columns;
      const row = Math.floor(index / columns);
      return {
        key,
        x: ((column + 0.5) / columns) * 100,
        y: ((row + 0.5) / rows) * 100,
        rotation: ((index * 17) % 13) - 6,
        scale: 1,
        z: index,
      };
    });
  }
  function renamePage(pageId: string, title: string) {
    requestId.current = null;
    setUndo((previous) => previous || { state: activeOrg, ops });
    setDraft(applyOperation(activeOrg, { op: 'rename_page', pageId, title }));
    setOps((previous) => {
      const next = [...previous];
      let index = -1;
      for (let i = next.length - 1; i >= 0; i -= 1) {
        if (next[i].op === 'rename_page' && next[i].pageId === pageId) {
          index = i;
          break;
        }
      }
      const operation = { op: 'rename_page', pageId, title };
      if (index < 0) next.push(operation);
      else next[index] = operation;
      return next;
    });
    setSaveError('');
  }
  function startArrange() {
    if (view === 'explore') {
      const canvas = seedCanvas(data?.items || [], org.canvas || [], data?.orderedKeys || []);
      setDraft({ ...org, canvas });
      setOps([{ op: 'set_canvas', positions: canvas }]);
      setSelectedKeys([]);
      setNotice('Drag passports to make the collection yours. Save when it feels right.');
      return;
    }
    setDraft({ ...org, order: data?.orderedKeys || org.order });
    setOps(page ? [] : [{ op: 'initialize_order' }]);
    setSelectedKeys([]);
    setNotice('Choose cards to place on a page, or use Move to arrange them.');
    modify({ types: '', type: '', q: '', sort: 'custom', group: 'none', view: 'binder' });
  }
  function editCanvasPosition(position: CanvasPosition) {
    if (!draft) return;
    const next = [...(activeOrg.canvas || [])];
    const index = next.findIndex((item) => item.key === position.key);
    if (index < 0) next.push(position);
    else next[index] = position;
    const operation: Operation = { op: 'set_canvas', positions: next };
    setUndo({ state: activeOrg, ops });
    setDraft({ ...activeOrg, canvas: next });
    setOps((previous) => [
      ...previous.filter((item) => item.op !== 'set_canvas'),
      operation,
    ]);
    requestId.current = null;
    setSaveError('');
  }
  function cancelArrange() {
    setDraft(null);
    setOps([]);
    setUndo(null);
    setSaveError('');
    setSelectedKeys([]);
    requestId.current = null;
  }
  async function save() {
    if (!draft || !ops.length) return;
    setSaving(true);
    setSaveError('');
    requestId.current ||= crypto.randomUUID();
    try {
      const saved = await collectionRequest<Organization>(new URLSearchParams(), {
        expected_revision: draft.revision,
        command_id: requestId.current,
        operations: ops,
      });
      setOrg(saved);
      cancelArrange();
      setConflict(false);
      setNotice('Arrangement saved.');
      setRevision((v) => v + 1);
    } catch (e) {
      setSaveError((e as Error).message);
      setConflict((e as CollectionError).code === 'conflict');
    } finally {
      setSaving(false);
    }
  }
  async function loadMore() {
    if (!data?.nextCursor) return;
    const id = generation.current;
    setBusy(true);
    try {
      const p = new URLSearchParams(queryString);
      p.set('cursor', data.nextCursor);
      const r = await collectionRequest<CollectionResult>(p);
      if (id !== generation.current) return;
      r.items.forEach((c) => cache.current.set(c.key!, c));
      setData((old) =>
        old
          ? {
              ...r,
              items: Array.from(
                new Map([...old.items, ...r.items].map((c) => [c.key, c])).values()
              ),
            }
          : r
      );
    } catch (e) {
      if (id === generation.current) setError((e as Error).message);
    } finally {
      if (id === generation.current) setBusy(false);
    }
  }
  function chooseType(type: string) {
    modify({
      types: types.includes(type)
        ? types.filter((k) => k !== type).join(',')
        : [...types, type].join(','),
      type: '',
    });
  }
  function newPage() {
    if (!pageName.trim()) return;
    edit({ op: 'create_page', clientId: crypto.randomUUID(), title: pageName.trim() });
    setPageName('');
    setNotice('Page created in your draft. Save arrangement to keep it.');
  }
  function placeSelection() {
    if (!targetPage || !selectedKeys.length) return;
    let next = activeOrg;
    const additions = selectedKeys.map((cardKey) => ({
      op: 'place',
      pageId: targetPage,
      cardKey,
      beforeKey: null,
    }));
    setUndo({ state: activeOrg, ops });
    additions.forEach((op) => {
      next = applyOperation(next, op);
    });
    setDraft(next);
    setOps((o) => [...o, ...additions]);
    requestId.current = null;
    setSelectedKeys([]);
    setNotice('Placed on your page. Save arrangement to keep it.');
  }
  async function showcase(replace?: string) {
    if (!selected?.key || !data) return;
    const old = data.preferences.showcase;
    const keys = old.includes(selected.key)
      ? old.filter((k) => k !== selected.key)
      : replace
        ? [...old.filter((k) => k !== replace), selected.key]
        : [...old, selected.key];
    if (keys.length > 3) {
      setShowcasePicker(true);
      return;
    }
    try {
      const preferences = await cardRequest<CollectionResult['preferences']>('preferences', {
        showcase: keys,
      });
      setData({ ...data, preferences });
      setNotice('Showcase updated.');
      setShowcasePicker(false);
      setRevision((v) => v + 1);
    } catch (e) {
      setDetailError((e as Error).message);
    }
  }
  const renderCard = (c: Card) => (
    <div
      className="bc-sleeve"
      data-binder-key={c.key}
      key={c.key}
      data-selected={selectedKey === c.key || selectedKeys.includes(c.key!)}
      data-kind={c.subject_type}
    >
      {draft && (
        <div className="bc-card-arrange">
          <label>
            <input
              type="checkbox"
              checked={selectedKeys.includes(c.key!)}
              onChange={() =>
                setSelectedKeys((v) =>
                  v.includes(c.key!) ? v.filter((k) => k !== c.key) : [...v, c.key!]
                )
              }
            />{' '}
            Select
          </label>
          <button
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('text/binder-card', c.key!);
              e.dataTransfer.effectAllowed = 'move';
            }}
            aria-label={`Drag ${c.title}`}
          >
            ⠿
          </button>
        </div>
      )}
      <div
        onDragOver={(e) => {
          if (draft) e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          const key = e.dataTransfer.getData('text/binder-card');
          if (draft && cache.current.has(key))
            edit({ op: 'move', pageId: page || 'all', cardKey: key, beforeKey: c.key! });
        }}
      >
        <CollectionCardFace
          card={c}
          onOpen={() => inspect(c)}
          openLabel="Inspect passport"
          arrival={arrival === c.key}
          quickReveal={data?.preferences.quick_reveal}
        />
      </div>
      <div className="bc-sleeve-label">
        <span>{kindLabel(c.subject_type)}</span>
        <span>
          {c.earned_at
            ? new Date(c.earned_at.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, {
                month: 'short',
                year: 'numeric',
              })
            : ''}
        </span>
      </div>
      {draft && (
        <label className="bc-move">
          Move{' '}
          <select
            aria-label={`Move ${c.title}`}
            value=""
            onChange={(e) => {
              if (e.target.value)
                edit({
                  op: 'move',
                  pageId: page || 'all',
                  cardKey: c.key!,
                  beforeKey: e.target.value === 'end' ? null : e.target.value,
                });
            }}
          >
            <option value="">Choose position</option>
            {items
              .filter((t) => t.key !== c.key)
              .map((t) => (
                <option key={t.key} value={t.key}>
                  Before {t.title}
                </option>
              ))}
            <option value="end">To end</option>
          </select>
        </label>
      )}
      {draft && page && (
        <button
          className="bc-text-button"
          onClick={() => edit({ op: 'remove_from_page', pageId: page, cardKey: c.key! })}
        >
          Remove from page
        </button>
      )}
    </div>
  );
  if (fallback) return <PassportHub view={entrance ? 'overview' : 'collection'} />;
  const groups = data?.groups || [];
  const editionCount = data?.totals.earnedEditions || 0;
  return (
    <div className="bc-page" ref={root}>
      <div className="bc-topline">
        <Link to="/passport">Bilt:Spec Passport</Link>
        <PassportScanButton />
      </div>
      {entrance && (
        <section className="bc-entrance" aria-label="Your Passport showcase">
          <div className="bc-intro">
            <span className="bc-caption">Your Passport</span>
            <h1>
              Every scan,
              <br />
              <em>kept.</em>
            </h1>
            <p>
              The cars. The places. The moments.
              <br />A collection that could only be yours.
            </p>
            <button
              className="bc-primary"
              onClick={() =>
                surface.current?.scrollIntoView({
                  behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                    ? 'auto'
                    : 'smooth',
                  block: 'start',
                })
              }
            >
              Open collection <span aria-hidden>↗</span>
            </button>
            {data && (
              <div className="bc-count">
                <strong>{data.totals.basePassports}</strong> passports <span>·</span> {editionCount}{' '}
                editions
              </div>
            )}
          </div>
          <div className="bc-display">
            <div className="bc-display-sheet" aria-hidden="true">
              <div className="bc-sheet-spine" />
              <div className="bc-sheet-header">
                <span>BILT:SPEC</span>
                <span>FIELD ARCHIVE · 01</span>
              </div>
              <div className="bc-sheet-title">PASSPORT BINDER</div>
              <div className="bc-sheet-slots">
                <span />
                <span />
                <span />
              </div>
              <div className="bc-sheet-footer">PERSONAL COLLECTION · KEEP THE GOOD FINDS</div>
            </div>
            <div className="bc-display-cards">
              {data?.showcase.length ? (
                data.showcase.slice(0, 3).map((c, i) => (
                  <div
                    className="bc-display-card"
                    key={c.key}
                    style={{ '--card-index': i } as React.CSSProperties}
                  >
                    <CollectorCard
                      card={c}
                      onOpen={() => inspect(c)}
                      openLabel="Inspect passport"
                    />
                  </div>
                ))
              ) : (
                <div className="bc-reserved">
                  <span>YOUR FIRST FIND</span>
                  <strong>
                    A place for
                    <br />
                    something good.
                  </strong>
                  <p>Scan a Bilt QR to begin.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
      <section className="bc-collection" ref={surface} aria-label="Your collection">
        <div className="bc-controls">
          <div className="bc-control-top">
            <div>
              <h2>
                Your collection<span className="bc-total">{data?.totalMatching ?? '…'}</span>
              </h2>
              <p>Find a favorite. Open a stack. Make it yours.</p>
            </div>
            <label className="bc-search">
              <span className="bc-sr">Find a passport</span>
              <span aria-hidden>⌕</span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find a passport"
                disabled={!!draft && view !== 'explore'}
              />
            </label>
            <div className="bc-views" role="group" aria-label="Collection view">
              {(['explore', 'binder', 'list', 'stacks'] as View[]).map((v) => (
                <button
                  key={v}
                  aria-label={`${v === 'explore' ? 'Explore' : v[0].toUpperCase() + v.slice(1)} view`}
                  aria-pressed={view === v}
                  onClick={() =>
                    modify({
                      view: v,
                      ...(v === 'stacks' && group === 'none' ? { group: 'type' } : {}),
                    })
                  }
                >
                  {v === 'binder' ? '▦' : v === 'stacks' ? '▱' : v === 'explore' ? '◎' : '☷'}{' '}
                  <span>{v === 'explore' ? 'Explore' : v[0].toUpperCase() + v.slice(1)}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="bc-types" aria-label="Passport types">
            <button
              aria-pressed={!types.length}
              onClick={() => modify({ types: '', type: '' })}
              disabled={!!draft && view !== 'explore'}
            >
              All passports
            </button>
            {(data?.typeFacets || [])
              .filter((f, i) => allTypes || i < 5 || types.includes(f.kind))
              .map((f) => (
                <button
                  key={f.kind}
                  disabled={!!draft && view !== 'explore'}
                  aria-pressed={types.includes(f.kind)}
                  onClick={() => chooseType(f.kind)}
                >
                  {f.label}
                  <span>{f.count}</span>
                </button>
              ))}
            <button onClick={() => setAllTypes((v) => !v)} aria-expanded={allTypes}>
              {allTypes ? 'Fewer types' : 'More types'} {allTypes ? '−' : '+'}
            </button>
          </div>
          <div className="bc-tools">
            <button
              className="bc-mobile-filter"
              onClick={() => setFilters((v) => !v)}
              aria-expanded={filters}
            >
              Filters & sort
            </button>
            <div className={`bc-selects ${filters ? 'is-open' : ''}`}>
              <label>
                Page
                <select
                  value={page}
                  disabled={!!draft && view !== 'explore'}
                  onChange={(e) =>
                    modify({
                      page: e.target.value,
                      sort: e.target.value ? 'custom' : org.defaults.sort,
                    })
                  }
                >
                  <option value="">All passports</option>
                  {org.pages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Group
                <select
                  value={group}
                  disabled={!!draft && view !== 'explore'}
                  onChange={(e) => {
                    setOpen([]);
                    modify({
                      group: e.target.value,
                      ...(e.target.value !== 'none' ? { view: 'stacks' } : {}),
                    });
                  }}
                >
                  <option value="none">None</option>
                  <option value="type">Passport type</option>
                  <option value="month">Collected month</option>
                  <option value="pages">My pages</option>
                </select>
              </label>
              <label>
                Sort
                <select
                  value={query.get('sort') || 'newest'}
                  disabled={!!draft && view !== 'explore'}
                  onChange={(e) => modify({ sort: e.target.value })}
                >
                  <option value="newest">Newest collected</option>
                  <option value="oldest">First collected</option>
                  <option value="name">Name</option>
                  <option value="custom">My order</option>
                </select>
              </label>
              <label>
                Edition
                <select
                  value={query.get('edition') || 'all'}
                  disabled={!!draft && view !== 'explore'}
                  onChange={(e) => modify({ edition: e.target.value })}
                >
                  <option value="all">All passports</option>
                  <option value="base">Base passports</option>
                  <option value="earned">Earned editions</option>
                </select>
              </label>
            </div>
            <button
              className="bc-arrange-button"
              onClick={draft ? cancelArrange : startArrange}
              disabled={!data || saving}
            >
              {draft ? 'Cancel arrangement' : view === 'explore' ? 'Arrange layout' : 'Arrange'}
            </button>
          </div>
        </div>
        {draft && view !== 'explore' && (
          <div className="bc-arrange-panel">
            <div>
              <strong>Make room for your favorites.</strong>
              <p>Select passports and place them on a personal page. Your scans stay collected.</p>
            </div>
            <div className="bc-arrange-fields">
              <label>
                New page
                <input
                  maxLength={80}
                  value={pageName}
                  placeholder="Name your page"
                  onChange={(e) => setPageName(e.target.value)}
                />
              </label>
              <button onClick={newPage} disabled={!pageName.trim()}>
                Create page
              </button>
              <label>
                Place selected on
                <select value={targetPage} onChange={(e) => setTargetPage(e.target.value)}>
                  <option value="">Choose a page</option>
                  {activeOrg.pages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </label>
              <button onClick={placeSelection} disabled={!targetPage || !selectedKeys.length}>
                Place {selectedKeys.length || ''}
              </button>
            </div>
            <div className="bc-page-management">
              {activeOrg.pages.map((p) => (
                <div key={p.id}>
                  <input
                    aria-label={`Page name ${p.title}`}
                    value={p.title}
                    maxLength={80}
                    onChange={(e) => renamePage(p.id, e.target.value)}
                  />
                  <span>{p.keys.length} cards</span>
                  <button onClick={() => edit({ op: 'remove_page', pageId: p.id })}>
                    Remove page
                  </button>
                </div>
              ))}
            </div>
            <div className="bc-save-row">
              <button
                onClick={() => {
                  if (undo) {
                    setDraft(undo.state);
                    setOps(undo.ops);
                    setUndo(null);
                    requestId.current = null;
                  }
                }}
                disabled={!undo || saving}
              >
                Undo
              </button>
              <button
                onClick={() =>
                  edit({ op: 'set_defaults', view, group, sort: query.get('sort') || 'newest' })
                }
              >
                Remember this view
              </button>
              <button className="bc-primary" disabled={!ops.length || saving} onClick={save}>
                {saving ? 'Saving…' : 'Save arrangement'}
              </button>
            </div>
            {saveError && (
              <div role="alert">
                {saveError}
                {conflict && (
                  <button
                    onClick={() => {
                      cancelArrange();
                      setRevision((v) => v + 1);
                    }}
                  >
                    Reload saved version
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        <div className="bc-status" role="status">
          {busy ? 'Opening your collection…' : notice}
        </div>
        {error && (
          <div className="bc-error" role="alert">
            {error}
            <button onClick={() => setRevision((v) => v + 1)}>Try again</button>
          </div>
        )}
        {!data && !error && (
          <div className="bc-grid bc-loading" aria-hidden>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} />
            ))}
          </div>
        )}
        {data && view === 'explore' ? (
          <PassportExploreCanvas
            cards={items}
            positions={activeOrg.canvas || []}
            editable={!!draft}
            onOpen={inspect}
            onPositionChange={editCanvasPosition}
            onSave={save}
            onCancel={cancelArrange}
            saving={saving}
            saveError={saveError}
          />
        ) : data && view === 'stacks' && groups.length > 0 ? (
          <>
            <div className="bc-stack-tools">
              <span>{groups.length} stacks</span>
              <button onClick={() => setOpen(groups.map((g) => g.key))}>Open all</button>
              <button onClick={() => setOpen([])}>Gather all</button>
            </div>
            <div className="bc-stacks">
              {groups.map((g) => (
                <StackGroup
                  key={`${queryString}:${g.key}`}
                  group={g}
                  query={queryString}
                  opened={open.includes(g.key)}
                  toggle={() =>
                    setOpen((v) =>
                      v.includes(g.key) ? v.filter((k) => k !== g.key) : [...v, g.key]
                    )
                  }
                  inspect={inspect}
                  cache={cache.current}
                />
              ))}
            </div>
          </>
        ) : view === 'list' ? (
          <div className="bc-list">
            {items.map((c) => (
              <button key={c.key} onClick={(e) => inspect(c, e)} data-binder-key={c.key}>
                <span className="bc-list-image">
                  {cardImage(c) && <img src={cardImage(c)} alt="" loading="lazy" />}
                </span>
                <span>
                  <strong>{c.title}</strong>
                  <small>
                    {kindLabel(c.subject_type)} · {c.issuer}
                  </small>
                </span>
                <time>{c.earned_at?.slice(0, 10)}</time>
                <span aria-hidden>↗</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="bc-grid">{items.map(renderCard)}</div>
        )}
        {data && !items.length && (
          <div className="bc-empty">
            <span aria-hidden>▱</span>
            <h3>
              {types.length || search || page
                ? 'Nothing in this view yet.'
                : 'Your first find is out there.'}
            </h3>
            <p>
              {types.length || search
                ? 'Try another type or clear your search.'
                : page
                  ? 'Arrange your collection to place owned passports on this page.'
                  : 'Scan a Bilt QR on a car, at a place, or at an event. We’ll keep the passport here.'}
            </p>
            {types.length || search ? (
              <button onClick={() => modify({ types: '', type: '', q: '' })}>Clear filters</button>
            ) : page ? (
              <button onClick={startArrange}>Arrange collection</button>
            ) : (
              <PassportScanButton />
            )}
          </div>
        )}
        {data?.nextCursor && view !== 'stacks' && (
          <button className="bc-load" disabled={busy} onClick={loadMore}>
            Load more passports
          </button>
        )}
        <div className="bc-resolution">
          <div>
            <span className="bc-caption">Collected by you</span>
            <h3>
              A collection.
              <br />
              Your point of view.
            </h3>
          </div>
          <div>
            <Link to="/passport/sets">Official sets ↗</Link>
            <Link to="/passport/rewards">Your rewards ↗</Link>
          </div>
        </div>
        <footer className="bc-footer">
          <Link to="/passport/archive">Photo archive</Link>
          <Link to="/passport/settings">Privacy & export</Link>
          <Link to="/passport/recap">Recap</Link>
          <span>{ops.length ? 'Arrangement not saved' : 'Your passports, kept.'}</span>
        </footer>
      </section>
      <dialog
        className="bc-inspector"
        ref={dialog}
        onCancel={(e) => {
          e.preventDefault();
          closeInspector();
        }}
      >
        <div className="bc-inspector-controls">
          <span>YOUR PASSPORT</span>
          <button onClick={() => setBack((v) => !v)} disabled={!selected}>
            {back ? 'Show front' : 'Flip card'} ↻
          </button>
          <button data-close onClick={closeInspector}>
            Close ×
          </button>
        </div>
        {detailError && <p role="alert">{detailError}</p>}
        {selected ? (
          <div className="bc-inspector-body">
            <div className="bc-inspector-card" ref={detailCard}>
              <PassportCard
                card={selected}
                back={back}
                onFlip={() => setBack((v) => !v)}
                quiet
                readableStory
              />
            </div>
            <div className="bc-inspector-info">
              <span className="bc-caption">{kindLabel(selected.subject_type)}</span>
              <h2>{selected.title}</h2>
              <p>{selected.design.story || 'A car, a place, a moment. Kept in your Passport.'}</p>
              <dl>
                <div>
                  <dt>Collected</dt>
                  <dd>{selected.earned_at?.slice(0, 10) || 'Not available'}</dd>
                </div>
                <div>
                  <dt>Record</dt>
                  <dd>
                    {selected.ownership_status === 'active'
                      ? cardEvidenceLabel(selected.evidence)
                      : selected.ownership_status}
                  </dd>
                </div>
                <div>
                  <dt>Issuer</dt>
                  <dd>{selected.issuer}</dd>
                </div>
                {selected.ordinal && (
                  <div>
                    <dt>Your edition</dt>
                    <dd>
                      {selected.ordinal}
                      {selected.rules.cap ? ` / ${selected.rules.cap}` : ''}
                    </dd>
                  </div>
                )}
              </dl>
              {!!selected.editions?.length && (
                <div className="bc-editions">
                  <h3>Earned editions</h3>
                  {selected.editions.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => {
                        cache.current.set(c.key!, c);
                        modify({ card: c.key! });
                      }}
                    >
                      {c.title} {c.ordinal ? `· #${c.ordinal}` : ''}
                    </button>
                  ))}
                </div>
              )}
              {selected.subject?.subject_ref && (
                <PassportVisitHistory subjectRef={selected.subject.subject_ref} />
              )}
              <div className="bc-inspector-actions">
                {selected.ownership_status === 'active' && (
                  <>
                    <button onClick={() => showcase()}>
                      {data?.preferences.showcase.includes(selected.key!)
                        ? 'Remove from showcase'
                        : 'Display in showcase'}
                    </button>
                    <button
                      onClick={() => {
                        if (!draft) startArrange();
                        setSelectedKeys([selected.key!]);
                        closeInspector();
                      }}
                    >
                      Place on page
                    </button>
                  </>
                )}
                {cardSubjectPath(selected) && (
                  <Link to={cardSubjectPath(selected)!}>View source ↗</Link>
                )}
                <Link to={`/passport/export-card?key=${encodeURIComponent(selected.key!)}`}>
                  Export card ↗
                </Link>
              </div>
              {showcasePicker && (
                <div>
                  <p>Choose a showcase card to replace.</p>
                  {data?.preferences.showcase.map((k) => (
                    <button key={k} onClick={() => showcase(k)}>
                      {cache.current.get(k)?.title || 'Showcase card'}
                    </button>
                  ))}
                </div>
              )}
              <div className="bc-inspector-nav">
                {['Previous', 'Next'].map((label, i) => {
                  const index = items.findIndex((c) => c.key === selected.key) + (i ? 1 : -1);
                  return (
                    <button
                      key={label}
                      disabled={index < 0 || index >= items.length}
                      onClick={() => modify({ card: items[index].key! })}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          !detailError && <p role="status">Opening the card…</p>
        )}
      </dialog>
    </div>
  );
}
function StackGroup({
  group,
  query,
  opened,
  toggle,
  inspect,
  cache,
}: {
  group: Group;
  query: string;
  opened: boolean;
  toggle: () => void;
  inspect: (c: Card) => void;
  cache: Map<string, Card>;
}) {
  const [data, setData] = useState<CollectionResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const gen = useRef(0);
  const load = useCallback(
    async (cursor?: string) => {
      const id = ++gen.current;
      setBusy(true);
      setError('');
      try {
        const p = new URLSearchParams(query);
        p.set('groupKey', group.key);
        if (cursor) p.set('cursor', cursor);
        const r = await collectionRequest<CollectionResult>(p);
        if (id !== gen.current) return;
        r.items.forEach((c) => cache.set(c.key!, c));
        setData((old) => (cursor && old ? { ...r, items: [...old.items, ...r.items] } : r));
      } catch (e) {
        if (id === gen.current) setError((e as Error).message);
      } finally {
        if (id === gen.current) setBusy(false);
      }
    },
    [query, group.key, cache]
  );
  useEffect(() => {
    if (opened && !data) void load();
  }, [opened, data, load]);
  useEffect(
    () => () => {
      gen.current++;
    },
    []
  );
  useCollectionMotion(root, `${opened && !!data}:${data?.items.length}`);
  return (
    <div ref={root} className={`bc-stack ${opened ? 'is-open' : ''}`} data-kind={group.key}>
      <button className="bc-stack-heading" onClick={toggle} aria-expanded={opened}>
        <span className="bc-mini-stack" aria-hidden>
          {[2, 1, 0].map((i) => (
            <span key={i} style={{ '--stack-index': i } as React.CSSProperties}>
              {cache.get(group.coverKeys[i]) && cardImage(cache.get(group.coverKeys[i])!) ? (
                <img src={cardImage(cache.get(group.coverKeys[i])!)} alt="" />
              ) : (
                <span className="bc-stack-mark">B:</span>
              )}
            </span>
          ))}
        </span>
        <span>
          <strong>{group.label}</strong>
          <small>{group.count} passports</small>
        </span>
        <span>{opened ? 'Gather stack −' : 'Open stack ↗'}</span>
      </button>
      {opened && (
        <div className="bc-stack-sheet">
          {busy && !data && <p role="status">Opening the sleeves…</p>}
          {error && (
            <p role="alert">
              {error}
              <button onClick={() => load()}>Try again</button>
            </p>
          )}
          <div className="bc-grid">
            {data?.items.map((c) => (
              <div className="bc-sleeve" data-binder-key={c.key} key={c.key}>
                <CollectionCardFace
                  card={c}
                  onOpen={() => inspect(c)}
                  openLabel="Inspect passport"
                />
              </div>
            ))}
          </div>
          {data?.nextCursor && (
            <button className="bc-load" disabled={busy} onClick={() => load(data.nextCursor!)}>
              More in this stack
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CollectionCardFace(props: React.ComponentProps<typeof CollectorCard>) {
  const c = props.card;
  return (
    <>
      <div className="bc-full-card">
        <CollectorCard {...props} />
      </div>
      <button className="bc-compact-card" onClick={props.onOpen} aria-label={`Inspect ${c.title}`}>
        <span className="bc-compact-art">
          {cardImage(c) ? <img src={cardImage(c)} alt="" loading="lazy" /> : <span>B:</span>}
        </span>
        <span className="bc-compact-title">{c.title}</span>
        <span className="bc-compact-kind">
          {kindLabel(c.subject_type)} <span aria-hidden>↗</span>
        </span>
      </button>
    </>
  );
}
